import { FC, memo } from 'react';

import { Box, Flex, Stack, useMediaQuery } from '@ui';

import { AdaptiveFieldByType } from './AdaptiveFieldByType';
import { ConditionalField } from './ConditionalField';
import { AdaptiveFormConfig, AdaptiveFormFieldConfig, Fields } from './types';

const FULL_ROW_FIELDS = [
  Fields.title,
  Fields.sectionTitle,
  Fields.sectionContainer,
  Fields.columnsContainer,
  Fields.divider,
  Fields.custom,
  Fields.alert,
  Fields.accordionSection,
  Fields.arrayForm,
  Fields.membersAccordion,
  Fields.switchGroup,
  Fields.nomineeSelect,
  Fields.switch
];

interface FieldWrapperProps {
  field: AdaptiveFormFieldConfig;
  context: any;
  columns?: number;
}

const FieldWrapper = memo<FieldWrapperProps>(({ field, context, columns }) => {
  const isMobile = useMediaQuery('(max-width: 600px)');

  if (field.hidden) {
    return null;
  }

  const isFullRow =
    field.fullRow ||
    field.sectionBreak ||
    FULL_ROW_FIELDS.includes(field.type) ||
    field.fields ||
    field.getFields ||
    isMobile;

  // Calculate flexBasis based on columns prop or default behavior
  let flexBasis: string;
  if (isMobile) {
    flexBasis = '100%';
  } else if (columns) {
    // Custom columns layout
    const gap = 24;
    const gapAdjustment = (gap * (columns - 1)) / columns;
    flexBasis = `calc(${100 / columns}% - ${gapAdjustment}px)`;
  } else {
    // Default behavior
    flexBasis = isFullRow ? '100%' : 'calc(50% - 12px)';
  }

  const wrapperStyle = {
    flexBasis,
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 0,
    alignSelf: field.wrapperStyle?.alignSelf ?? 'flex-start',
    ...(isFullRow && !columns && { width: '100%' })
  };

  if (field.condition) {
    return (
      <ConditionalField
        config={field}
        context={context}
        wrapperStyle={wrapperStyle}
      />
    );
  }

  return (
    <Box style={wrapperStyle}>
      <AdaptiveFieldByType config={field} context={context} />
    </Box>
  );
});

FieldWrapper.displayName = 'FieldWrapper';

interface AllFieldsProps {
  fields: AdaptiveFormConfig;
  context: any;
  columns?: number;
}

const AllFields = memo<AllFieldsProps>(({ fields, context, columns }) => {
  return (
    <Flex gap={{ base: 20, sm: 24, md: 24 }} wrap="wrap" align="center">
      {fields?.map((field, index) => (
        <FieldWrapper
          key={`${field.type}.${index}.${field.name}`}
          field={field}
          context={context}
          columns={columns}
        />
      ))}
    </Flex>
  );
});

AllFields.displayName = 'AllFields';

export interface AdaptiveFormRowsProps {
  config: AdaptiveFormConfig;
  context?: any;
  withDefaultWrapper?: boolean;
  columns?: number;
}

/**
 * @description Renders form fields in a flexible layout using Flexbox with wrap.
 *
 * **Field Width Control:**
 * - Default: `calc(50% - 12px)` - 2 fields per row
 * - Custom columns: `calc(${100/columns}% - gap)` when columns prop is provided
 * - Full row: `100%` width when:
 *   - `field.fullRow === true`
 *   - `field.sectionBreak === true`
 *   - Field type is in FULL_ROW_FIELDS (title, switch, accordion, etc.)
 *   - Field has nested fields (`field.fields` or `field.getFields`)
 *   - Mobile viewport (max-width: 600px)
 *
 * **Special Behaviors:**
 * - Hidden fields (`field.hidden`) render nothing - no blank spaces
 * - Conditional fields receive wrapper styles for consistent sizing
 *
 * @param config - Array of field configurations
 * @param context - Form context passed to all fields
 * @param withDefaultWrapper - Wraps content in Stack container with gap (default: true)
 * @param columns - Number of columns for custom layout (e.g., 3 for 3-column layout)
 */
export const AdaptiveFormRows: FC<AdaptiveFormRowsProps> = ({
  config,
  context,
  withDefaultWrapper = true,
  columns
}) => {
  const content = <AllFields fields={config} context={context} columns={columns} />;

  return withDefaultWrapper ? (
    <Stack gap={24} w="100%">
      {content}
    </Stack>
  ) : (
    content
  );
};
